<template>
  <b-card class="-mt-[8px]">
    <div class="flex items-center justify-between flex-wrap flex-col sm:flex-row gap-[16px]">
      <h3 class="text-xl text-black font-semibold w-full sm:w-auto">
        Daftar Request Penyimpanan
      </h3>
      <div class="flex items-center justify-end gap-[8px] grow flex-col sm:flex-row w-full sm:w-auto">
        <date-picker
          v-model="dateRange"
          class="w-full sm:w-[300px]"
          :ranges="datePickerRangeGeneral"
          :date-format="createDatePickerRestrictor()"
        />
        <infinite-select
          v-model="partner"
          class="w-full sm:!w-[220px]"
          placeholder="Semua Partner"
          option-label="name"
          default-value=""
          :options="list_partner"
          :has-next-page="hasNextPartners"
          :reduce="partner => partner.id"
          :debounce-timeout="500"
          :loading="loadingPartner"
          :clearable="false"
          @on-search="searchPartners"
          @on-intersect="getPartner"
          @on-close="resetSelectionPartner"
        >
          <template #optionValue="{value}">
            {{ value.name }}
            <span
              v-if="value.unsubscribe === 1"
              class="ml-1 unsubscribe-tag"
            >Unsubscribe</span>
          </template>
        </infinite-select>
        <b-form-select
          v-model="status"
          class="w-full sm:!w-[200px]"
          :options="statusOptions"
        />
        <b-button
          variant="ghost"
          size="sm"
          @click="refreshData"
        >
          <img
            v-b-tooltip.hover.top="'Refresh Data'"
            class="cursor-pointer"
            :class="{ 'rotate-animation': isRotating }"
            src="https://storage.googleapis.com/komerce/assets/icons/refresh-2.svg"
            alt="Komerce"
          >
        </b-button>
      </div>
    </div>

    <b-table
      id="table"
      show-empty
      empty-text="Tidak ada data yang ditampilkan."
      class="mt-2 text-center"
      :fields="fields"
      :items="fetcherSubmissions.data"
      responsive
    >
      <template #empty>
        <div class="text-center py-3">
          <div
            v-if="fetcherSubmissions.isLoading && fetcherSubmissions.data.length === 0"
            ref="loadingFooter"
            class="d-flex justify-content-center align-items-center"
          >
            <b-spinner
              variant="primary"
              label="Loading..."
            />
            <span class="ml-2">Memuat data...</span>
          </div>
          <div
            v-else
            class="text-muted py-2"
          >
            Tidak ada data yang ditampilkan
          </div>
        </div>
      </template>
      <template
        #cell(partner_name)="data"
      >
        <div class="d-flex">
          <span class="d-flex align-center">
            <b-avatar
              style="width: 40px; height: 40px;"
              rounded="circle"
              :src="data.item.image_logo_url"
            />
          </span>
          <span class="text-start ml-1">
            <div>{{ data.item.partner_name }}</div>
            <div class="text-[#828282] text-[13px]">{{ data.item.partner_city }}</div>
          </span>
        </div>
      </template>
      <template #cell(status)="data">
        <StorageRequestStatusCell :status="data.value" />
      </template>
      <template #cell(id)="data">
        <router-link
          :to="{ name: 'storage-request-detail', params: { id: data.item.id } }"
          class="hover:underline text-[#08a0f7] hover:text-[#08a0f7] cursor-pointer"
        >
          Lihat Detail
        </router-link>
      </template>
    </b-table>
    <div
      v-if="fetcherSubmissions"
      class="text-center py-3"
    >
      <div
        v-if="fetcherSubmissions.isLoading && fetcherSubmissions.data.length > 0"
        ref="loadingFooter"
        class="d-flex justify-content-center align-items-center"
      >
        <b-spinner
          variant="primary"
          label="Loading..."
        />
        <span class="ml-2">Memuat data...</span>
      </div>
      <div
        v-else-if="fetcherSubmissions.hasMore"
        ref="intersectionFooter"
        class="py-2"
      />
      <div
        v-else-if="fetcherSubmissions.data && fetcherSubmissions.data.length > 0"
        class="text-muted py-2"
      >
        Semua data telah dimuat
      </div>
    </div>
  </b-card>
</template>

<script>
import { firstDateOfMonth, lastDateOfMonth, today } from '@/store/helpers'
import InfiniteSelect from '@/views/components/select/InfiniteSelect.vue'
import { toastError } from '@/libs/toastification'
import { INBOUND, SELECT_PARTNERS } from '@/provider/url'
import provider from '@/provider/provider'
import { YEAR_MONTH_DAY, createDatePickerRestrictor } from '@/libs/filterDate'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { BSpinner } from 'bootstrap-vue'
import DatePicker from './DatePicker.vue'
import {
  datePickerRangeGeneral, storageRequestStatusOptions, storageRequestTableFields, storageRequestStatus,
} from './config'
import StorageRequestStatusCell from './StorageRequestStatusCell.vue'

export default {
  name: 'StorageRequest',
  components: {
    DatePicker,
    InfiniteSelect,
    BSpinner,
    StorageRequestStatusCell,
  },
  data() {
    return {
      title: 'Request Penyimpanan',
      dateRange: {
        startDate: firstDateOfMonth,
        endDate: lastDateOfMonth,
      },
      status: '',
      statusOptions: storageRequestStatusOptions,
      storageRequestStatus,
      partner: '',
      limitPartners: 25,
      offsetPartners: 0,
      searchTermPartners: '',
      list_partner: [],
      loadingPartner: false,
      hasNextPartners: false,
      isRotating: false,
      fields: storageRequestTableFields,
      fetcherSubmissions: undefined,
      observer: null,
      datePickerRangeGeneral: undefined,
    }
  },
  watch: {
    dateRange: {
      deep: true,
      handler() {
        this.handleFilterChange()
      },
    },
    status() {
      this.handleFilterChange()
    },
    partner() {
      this.handleFilterChange()
    },
  },
  created() {
    this.initializeFetcherSubmissions()
    this.datePickerRangeGeneral = datePickerRangeGeneral(today)
  },
  async mounted() {
    this.setupIntersectionObserver()
    await this.fetcherSubmissions.fetchData({
      reset: true,
      options: this.getFetcherOptions(),
    })
    await this.getPartner()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  updated() {
    this.observeFooter()
  },
  methods: {
    createDatePickerRestrictor,
    refreshData() {
      this.isRotating = true
      setTimeout(() => {
        this.isRotating = false
      }, 1000)
      this.fetcherSubmissions.fetchData({
        reset: true,
        options: this.getFetcherOptions(),
      })
    },
    getPartner(q) {
      if (q) {
        this.offsetPartners = 0
        this.searchTermPartners = q
      }
      provider
        .single(`${SELECT_PARTNERS}?limit=${this.limitPartners}&offset=${this.offsetPartners}&search=${this.searchTermPartners}`)
        .then(response => {
          const { data } = response
          this.loadingPartner = false
          this.hasNextPartners = data.length >= this.limitPartners
          if (q && data.length === 0) {
            this.list_partner = []
            return
          }
          if (this.offsetPartners === 0 || q) {
            this.list_partner = [{
              id: '',
              name: 'Semua Partner',
            },
            ...data]
            this.offsetPartners += this.limitPartners
            return
          }
          this.list_partner.push(...data)
          this.offsetPartners += this.limitPartners
        }).catch(() => {
          toastError('Gagal mengambil daftar partner. Silakan coba lagi nanti.')
          this.loadingPartner = false
          if (q) this.list_partner = []
          else {
            this.list_partner = [{
              id: '',
              name: 'Semua Partner',
            }]
          }
          this.hasNextPartners = false
        })
    },
    searchPartners(q) {
      if (!q) {
        this.resetSelectionPartner()
      }
      this.getPartner(q)
    },
    resetSelectionPartner() {
      this.searchTermPartners = ''
      this.offsetPartners = 0
    },
    initializeFetcherSubmissions() {
      const fnFetch = ({
        offset, limit, start_date, end_date, status, partner_id,
      }) => this.$http_new.get('/komship/api/v1/submissions/mitra', {
        params: {
          offset,
          limit,
          start_date,
          end_date,
          status,
          partner_id,
        },
      }).then(response => {
        const { data } = response.data
        return {
          data,
        }
      }).catch(() => {
        toastError('Gagal mengambil data request penyimpanan, silahkan coba lagi')
        return {
          data: [],
        }
      })

      this.fetcherSubmissions = createPaginatedFetcher(fnFetch, 0, 50)
    },
    getFetcherOptions() {
      return {
        start_date: YEAR_MONTH_DAY(this.dateRange.startDate),
        end_date: YEAR_MONTH_DAY(this.dateRange.endDate),
        status: this.status,
        partner_id: this.partner || '',
      }
    },
    handleFilterChange() {
      if (this.fetcherSubmissions) {
        this.fetcherSubmissions.fetchData({
          reset: true,
          options: this.getFetcherOptions(),
        })
      }
    },
    setupIntersectionObserver() {
      if (!this.observer) {
        this.observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (
                entry.isIntersecting
                && this.fetcherSubmissions
                && this.fetcherSubmissions.hasMore
                && !this.fetcherSubmissions.isLoading
              ) {
                this.fetcherSubmissions.fetchData({
                  options: this.getFetcherOptions(),
                })
              }
            })
          },
          {
            root: null,
            rootMargin: '0px',
            threshold: 0.1,
          },
        )
      }
      this.observeFooter()
    },
    observeFooter() {
      if (!this.observer) return

      this.observer.disconnect()

      this.$nextTick(() => {
        const footerElement = this.$refs.intersectionFooter
        if (footerElement && this.fetcherSubmissions && this.fetcherSubmissions.hasMore) {
          this.observer.observe(footerElement)
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';

@keyframes rotate-three-times {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(1080deg);
  }
}

.rotate-animation {
  animation: rotate-three-times 2s ease-in-out;
}
</style>
